import React, { useState, useEffect } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { Rating } from "primereact/rating";
import { Chart } from "primereact/chart";
import { Tag } from "primereact/tag";
import {
  PriorityTag,
  StatusTag,
  StatCard,
  MiniBarChart,
} from "./components/helper";
import AgentMgmt from "./AgentMgmt";
import SLA from "./SLA";
import Reports from "./Reports";
import CategoryMaster from "./CategoryMaster";
import { supportApi } from "../../services";
import { useSelector } from "react-redux";
import { selectUser } from "../../services/store/userSlice";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from '../../components/protected/ProtectedSection';
import { SUPPORT_ADMIN_OVERVIEW_TAB_VIEW, SUPPORT_ADMIN_ALLTICKETS_TAB_VIEW, SUPPORT_ADMIN_AGENT_TAB_VIEW, SUPPORT_ADMIN_CATEGORY_TAB_VIEW, SUPPORT_ADMIN_SLASETTING_TAB_VIEW, SUPPORT_ADMIN_REPORTS_TAB_VIEW } from "../../config/permissions";

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}, ${hh}:${min}:${ss}`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const [stats, setStats] = useState({
    total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0,
    newCount: 0, pending: 0, onHold: 0, breached: 0, csat: "—",
  });
  const [categoryData, setCategoryData] = useState([]);
  const [agentData, setAgentData] = useState([]);
  const [trendData, setTrendData] = useState({ labels: [], datasets: [] });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [agentFilter, setAgentFilter] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshingSla, setRefreshingSla] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  // Department scope
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState(null); // null = no selection = all assigned depts
  const [defaultDeptIds, setDefaultDeptIds] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(parseInt(searchParams.get("tab")) || 0);
  const [slaSelectedDept, setSlaSelectedDept] = useState(null);

  const STATUS_OPTIONS = [
    { label: "All Status", value: null },
    { label: "New", value: "New" },
    { label: "Open", value: "Open" },
    { label: "In Progress", value: "In Progress" },
    { label: "Pending", value: "Pending" },
    { label: "Resolved", value: "Resolved" },
    { label: "Closed", value: "Closed" },
  ];

  const handleFilterChange = (filterName, value) => {
    setPage(0);
    const deptStr = getEffectiveDeptStr();
    const dateFilters = {
      fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
      toDate: toDate ? toDate.toISOString().split("T")[0] : "",
    };
    if (filterName === 'status') {
      setStatusFilter(value);
      loadTickets({ status: value ?? "" }, 0);
      loadDashboardData(deptStr, dateFilters);
    } else if (filterName === 'agent') {
      setAgentFilter(value);
      loadTickets({ agentFilter: value ?? "" }, 0);
    } else if (filterName === 'priority') {
      setPriorityFilter(value);
      loadTickets({ priority: value ?? "" }, 0);
    } else if (filterName === 'fromDate') {
      setFromDate(value);
      dateFilters.fromDate = value ? value.toISOString().split("T")[0] : "";
      loadTickets({ fromDate: dateFilters.fromDate }, 0);
      loadDashboardData(deptStr, dateFilters);
    } else if (filterName === 'toDate') {
      setToDate(value);
      dateFilters.toDate = value ? value.toISOString().split("T")[0] : "";
      loadTickets({ toDate: dateFilters.toDate }, 0);
      loadDashboardData(deptStr, dateFilters);
    }
    saveFilterState();
  };

  const saveFilterState = () => {
    sessionStorage.setItem("supportAdminFilters", JSON.stringify({
      search, statusFilter, priorityFilter, agentFilter,
      fromDate: fromDate ? fromDate.toISOString() : null,
      toDate: toDate ? toDate.toISOString() : null,
      deptFilter, page, activeTabIndex,
    }));
  };

  useEffect(() => { saveFilterState(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, statusFilter, priorityFilter, agentFilter, fromDate, toDate, deptFilter, page, activeTabIndex]
  );

  const PRIORITY_OPTIONS = [
    { label: "All Priority", value: null },
    { label: "Critical", value: "Critical" },
    { label: "High", value: "High" },
    { label: "Medium", value: "Medium" },
    { label: "Low", value: "Low" },
  ];

  const getEffectiveDeptStr = (overrideIds) => {
    if (overrideIds !== undefined) return overrideIds;
    return (deptFilter?.length ? deptFilter : defaultDeptIds).join(",");
  };

  const loadDashboardData = async (deptStr = "", filters = {}) => {
    const param = {
      departmentId: deptStr,
      fromDate: filters.fromDate ?? "",
      toDate: filters.toDate ?? "",
    };
    try {
      const [statsRes, catRes, agentRes, trendRes] = await Promise.all([
        supportApi.getAdminDashboardStats(param),
        supportApi.getTicketsByCategory(param),
        supportApi.getAgentPerformance(param),
        supportApi.getMonthlyTrend(param),
      ]);
      const s = statsRes.data?.data ?? {};
      setStats({
        total: s.total ?? 0,
        open: s.open ?? 0,
        inProgress: s.inProgress ?? 0,
        resolved: s.resolved ?? 0,
        closed: s.closed ?? 0,
        newCount: s.newCount ?? 0,
        pending: s.pending ?? 0,
        onHold: s.onHold ?? 0,
        breached: s.slaBreached ?? 0,
        csat: s.avgCsat ? `${s.avgCsat}%` : "—",
      });
      setCategoryData(
        (Array.isArray(catRes.data?.data) ? catRes.data.data : []).map((c) => ({
          label: c.name, pct: c.pct ?? 0, color: c.color ?? "#2563a8", val: String(c.count ?? 0),
        }))
      );
      setAgentData(
        (Array.isArray(agentRes.data?.data) ? agentRes.data.data : []).map((a) => ({
          label: a.name, pct: a.pct ?? 0, color: a.color ?? "#22a06b", val: String(a.resolved ?? 0),
          deptCounts: a.deptCounts ?? null,
        }))
      );
      const trend = trendRes.data?.data ?? {};
      setTrendData({
        labels: trend.labels ?? [],
        datasets: [{ label: "Tickets", data: trend.data ?? [], backgroundColor: ["#3b82c4", "#2563a8", "#1e3a5f"], borderRadius: 4 }],
      });
    } catch {
      toast.error("Failed to load dashboard");
    }
  };

  const HIDE_STATUSES = ["Resolved", "Closed"];

  const loadTickets = async (filters = {}, pageNum = 0, forTab = null) => {
    setLoading(true);
    const tabIndex = forTab !== null ? forTab : activeTabIndex;
    try {
      const effectiveStatus = filters.status !== undefined ? filters.status : (statusFilter ?? "");
      const isExplicitStatus = !!effectiveStatus;
      const isOverview = tabIndex === 0;
      const res = await supportApi.getAllTickets({
        pageNum,
        pageSize: PAGE_SIZE,
        search: filters.search ?? search,
        status: isExplicitStatus ? effectiveStatus : "",
        excludeStatuses: (isOverview && !isExplicitStatus) ? HIDE_STATUSES.join(",") : "",
        priority: filters.priority !== undefined ? filters.priority : (priorityFilter ?? ""),
        assignedTo: filters.agentFilter !== undefined ? filters.agentFilter : (agentFilter ?? ""),
        fromDate: filters.fromDate ?? (fromDate ? fromDate.toISOString().split("T")[0] : ""),
        toDate: filters.toDate ?? (toDate ? toDate.toISOString().split("T")[0] : ""),
        departmentId: filters.deptFilter !== undefined ? filters.deptFilter : getEffectiveDeptStr(),
      });
      setTickets(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.totalElement ?? 0);
    } catch {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const onFilter = () => {
    setPage(0);
    const deptStr = getEffectiveDeptStr();
    const dateFilters = {
      fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
      toDate: toDate ? toDate.toISOString().split("T")[0] : "",
    };
    const effectiveStatus = statusFilter === "Closed" ? statusFilter : (statusFilter ?? "");
    loadTickets({ status: effectiveStatus }, 0);
    loadDashboardData(deptStr, dateFilters);
  };

  const onClear = () => {
    setSearch("");
    setStatusFilter(null);
    setPriorityFilter(null);
    setAgentFilter(null);
    setFromDate(null);
    setToDate(null);
    setPage(0);
    sessionStorage.removeItem("supportAdminFilters");
    const deptStr = getEffectiveDeptStr();
    loadTickets(
      { search: "", status: "", priority: "", agentFilter: "", fromDate: "", toDate: "", deptFilter: deptStr },
      0,
    );
    loadDashboardData(deptStr, {});
  };

  const onStatFilter = (statusVal) => {
    setStatusFilter(statusVal);
    setPage(0);
    loadTickets({ status: statusVal ?? "" }, 0);
  };

  const onExport = async () => {
    if (!totalRecords) {
      toast.warn("No tickets to export");
      return;
    }
    try {
      const res = await supportApi.getAllTickets({
        pageNum: 0,
        pageSize: totalRecords,
        search,
        status: statusFilter ?? "",
        priority: priorityFilter ?? "",
        assignedTo: agentFilter ?? "",
        fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
        toDate: toDate ? toDate.toISOString().split("T")[0] : "",
      });
      const all = Array.isArray(res.data?.data) ? res.data.data : tickets;
      const rows = all.map((r) => {
        const agentName = r.assignedAgent
          ? `${r.assignedAgent.firstName} ${r.assignedAgent.lastName}`.trim()
          : "";
        const dn = r.assignedAgent?.agentProfile?.displayName;
        const displayName = Array.isArray(dn) ? dn[0] : dn;
        return {
          "Ticket ID": r.ticketNo ?? "",
          Subject: r.subject ?? "",
          "Raised By": r.requester
            ? `${r.requester.firstName} ${r.requester.lastName}`.trim()
            : "",
          Agent: displayName ? `${agentName} - ${displayName}` : agentName,
          Category: r.category?.name ?? "",
          Priority: r.priority ?? "",
          Status: r.status ?? "",
          "SLA Remaining": r.slaRemaining ?? "",
          "SLA Breached": r.slaBreach ? "Yes" : "No",
          "CSAT Rating": r.csatRating ?? "",
          "Created At": r.createdAt
            ? new Date(r.createdAt).toLocaleDateString()
            : "",
          "Updated At": r.updatedAt
            ? new Date(r.updatedAt).toLocaleDateString()
            : "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tickets");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `support-tickets-${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch {
      toast.error("Failed to export tickets");
    }
  };

  const onClose = async (ticketId) => {
    try {
      await supportApi.closeTicket(ticketId);
      setTickets((p) =>
        p.map((t) => (t.id === ticketId ? { ...t, status: "Closed" } : t)),
      );
      toast.success("Ticket closed");
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  const onRefreshSla = async () => {
    setRefreshingSla(true);
    try {
      await supportApi.refreshAllSla();
      toast.success("SLA fields refreshed for all open tickets");
      loadTickets({}, page);
    } catch {
      toast.error("Failed to refresh SLA");
    } finally {
      setRefreshingSla(false);
    }
  };

  const currentUser = useSelector(selectUser);

  useEffect(() => {
    const saved = sessionStorage.getItem("supportAdminFilters");
    let restoredState = null;
    if (saved) {
      try { restoredState = JSON.parse(saved); } catch { restoredState = null; }
    }
    if (restoredState) {
      setSearch(restoredState.search || "");
      setStatusFilter(restoredState.statusFilter ?? null);
      setPriorityFilter(restoredState.priorityFilter ?? null);
      setAgentFilter(restoredState.agentFilter ?? null);
      setFromDate(restoredState.fromDate ? new Date(restoredState.fromDate) : null);
      setToDate(restoredState.toDate ? new Date(restoredState.toDate) : null);
      setDeptFilter(restoredState.deptFilter ?? null);
      setPage(restoredState.page || 0);
      setActiveTabIndex(restoredState.activeTabIndex || 0);
    }
    Promise.all([
      supportApi.getDepartments(),
      supportApi.getDomainLookupByKey('user_department'),
    ]).then(([deptRes, lookupRes]) => {
      const allDepts = Array.isArray(deptRes.data?.data) ? deptRes.data.data : [];
      if (allDepts.length === 0) { loadTickets({}, 0); return; }

      const lookupEntries = Array.isArray(lookupRes.data?.data) ? lookupRes.data.data : [];
      const userEntry = lookupEntries
        .flatMap((entry) => Array.isArray(entry.value) ? entry.value : [])
        .find((v) => v.userId === currentUser?.id);

      const filtered = userEntry
        ? allDepts.filter((d) => userEntry.department.includes(d.id))
        : allDepts;

      setDepartments(filtered.map((d) => ({ label: d.title, value: d.id })));

      const defaultIds = filtered.map((d) => d.id);
      setDefaultDeptIds(defaultIds);
      setSlaSelectedDept(defaultIds[0] ?? null);
      const initTab = restoredState?.activeTabIndex || 0;
      const initDeptStr = restoredState?.deptFilter?.length
        ? restoredState.deptFilter.join(",")
        : defaultIds.join(",");
      const initStatus = restoredState?.statusFilter ?? "";
      loadTickets({ deptFilter: initDeptStr, status: initStatus, page: restoredState?.page || 0 }, restoredState?.page || 0, initTab);
      loadDashboardData(initDeptStr, {
        fromDate: restoredState?.fromDate ? new Date(restoredState.fromDate).toISOString().split("T")[0] : "",
        toDate: restoredState?.toDate ? new Date(restoredState.toDate).toISOString().split("T")[0] : "",
      });
    }).catch(() => { loadTickets({}, 0); loadDashboardData(""); });

    // Load agents for filter dropdown
    supportApi
      .getAgents()
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setAgents([
          { label: "All Agents", value: null },
          ...list.map((a) => {
            const fullName = `${a.user?.firstName ?? ""} ${a.user?.lastName ?? ""}`.trim();
            const dn = Array.isArray(a.displayName) ? a.displayName[0] : a.displayName;
            return {
              label: dn ? `${fullName} - ${dn}` : fullName,
              value: a.userId,
            };
          }),
        ]);
      })
      .catch(() => { });

    // Dashboard stats/charts are loaded after departments resolve (with correct deptStr)
  }, []);

  const donutData = {
    labels: ["Open", "In Progress", "Resolved", "Closed", "New", "Pending", "On Hold", "SLA Breached"],
    datasets: [
      {
        data: [
          stats.open,
          stats.inProgress,
          stats.resolved,
          stats.closed ?? 0,
          stats.newCount ?? 0,
          stats.pending ?? 0,
          stats.onHold ?? 0,
          stats.breached,
        ],
        backgroundColor: ["#fb8c00", "#2563a8", "#22a06b", "#6b7e93", "#e53935", "#7c3aed", "#00695c", "#dc2626"],
        borderWidth: 0,
      },
    ],
  };

  const formatTicketAgent = (r) => {
    if (!r.assignedAgent) return r.assignedTo ?? '—';
    const name = `${r.assignedAgent.firstName} ${r.assignedAgent.lastName}`.trim();
    const dn = r.assignedAgent?.agentProfile?.displayName;
    const displayName = Array.isArray(dn) ? dn[0] : dn;
    return displayName ? `${name} - ${displayName}` : (name || (r.assignedTo ?? '—'));
  };

  const deptFilterStr = (deptFilter?.length ? deptFilter : defaultDeptIds).join(",");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <div>
          <h1 className="ess-page-title" style={{ marginBottom: 2 }}>Help &amp; Support Admin Dashboard</h1>
          <p className="ess-page-sub" style={{ marginTop: 0 }}>Full visibility across all teams, agents, and ticket activity</p>
        </div>
        {departments.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", whiteSpace: "nowrap" }}>Department:</label>
            {activeTabIndex === 4 ? (
              <Dropdown
                value={slaSelectedDept}
                options={departments}
                onChange={(e) => setSlaSelectedDept(e.value)}
                placeholder="Select department…"
                style={{ minWidth: 220 }}
              />
            ) : (
              <MultiSelect
                value={deptFilter ?? []}
                options={departments}
                onChange={(e) => {
                  const selected = e.value ?? [];
                  setDeptFilter(selected.length ? selected : null);
                  const effective = selected.length ? selected : defaultDeptIds;
                  const deptStr = effective.join(",");
                  const dateFilters = {
                    fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
                    toDate: toDate ? toDate.toISOString().split("T")[0] : "",
                  };
                  setPage(0);
                  loadTickets({ deptFilter: deptStr }, 0);
                  loadDashboardData(deptStr, dateFilters);
                }}
                placeholder="All Departments"
                maxSelectedLabels={0}
                selectedItemsLabel={(deptFilter ?? []).length === 1
                  ? `${departments.find((d) => d.value === (deptFilter ?? [])[0])?.label ?? '1 Department'} Selected`
                  : `${(deptFilter ?? []).length} Departments Selected`
                }
                style={{ minWidth: 220, maxWidth: 360 }}
              />
            )}
          </div>
        )}
      </div>

      <TabView activeIndex={activeTabIndex} onTabChange={(e) => {
        setActiveTabIndex(e.index);
        if (e.index === 0 || e.index === 1) {
          setPage(0);
          loadTickets({}, 0, e.index);
        }
      }}>
        {hasPermission(SUPPORT_ADMIN_OVERVIEW_TAB_VIEW) && (
          <TabPanel header="Overview">
            <div className="stat-grid stat-grid-6" style={{ marginBottom: 20 }}>
              <StatCard
                num={stats.total}
                label="Total (MTD)"
                colorClass="sc-blue"
              />
              <StatCard
                num={stats.open}
                label="Open"
                colorClass="sc-orange"
                onClick={() => onStatFilter("Open")}
                style={{ cursor: "pointer" }}
              />
              <StatCard
                num={stats.inProgress}
                label="In Progress"
                colorClass="sc-purple"
                onClick={() => onStatFilter("In Progress")}
                style={{ cursor: "pointer" }}
              />
              <StatCard
                num={stats.resolved}
                label="Resolved (MTD)"
                colorClass="sc-green"
                onClick={() => onStatFilter("Resolved")}
                style={{ cursor: "pointer" }}
              />
              <StatCard
                num={stats.breached}
                label="SLA Breached"
                colorClass="sc-red"
              />
              <StatCard num={stats.csat} label="Avg CSAT" colorClass="sc-blue" />
            </div>

            <div className="grid-3" style={{ marginBottom: 20 }}>
              <MiniBarChart title="Tickets by Category" rows={categoryData} />
              <div className="mini-chart">
                <div className="mini-chart-title">Status Distribution</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 110, height: 110, flexShrink: 0 }}>
                    <Chart
                      type="doughnut"
                      data={donutData}
                      options={{
                        plugins: {
                          legend: { display: false },
                          tooltip: { enabled: true },
                        },
                        cutout: "68%",
                        maintainAspectRatio: false,
                      }}
                      style={{ width: "110px", height: "110px" }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                    }}
                  >
                    {donutData.labels.map(
                      (label, i) =>
                        donutData.datasets[0].data[i] > 0 && (
                          <div
                            key={label}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background:
                                  donutData.datasets[0].backgroundColor[i],
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                fontSize: 11,
                                color: "var(--muted)",
                                flex: 1,
                              }}
                            >
                              {label}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "var(--navy)",
                              }}
                            >
                              {donutData.datasets[0].data[i]}
                            </span>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              </div>
              <MiniBarChart title="Agent Performance (MTD)" rows={agentData} departments={departments} />
            </div>

            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <strong style={{ fontSize: 13, color: "var(--navy)" }}>
                  All Tickets
                </strong>
                <div className="search-wrap">
                  <i className="pi pi-search" />
                  <InputText
                    placeholder="Search by Ticket ID or Subject…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onFilter()}
                    style={{ width: 220 }}
                  />
                </div>
                <Dropdown
                  value={statusFilter}
                  options={STATUS_OPTIONS}
                  onChange={(e) => handleFilterChange('status', e.value)}
                  placeholder="All Status"
                  style={{ minWidth: 130 }}
                />
                <Dropdown
                  value={agentFilter}
                  options={agents}
                  onChange={(e) => handleFilterChange('agent', e.value)}
                  placeholder="All Agents"
                  style={{ minWidth: 150 }}
                />
                <Dropdown
                  value={priorityFilter}
                  options={PRIORITY_OPTIONS}
                  onChange={(e) => handleFilterChange('priority', e.value)}
                  placeholder="All Priority"
                  style={{ minWidth: 130 }}
                />
                <Calendar
                  value={fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.value)}
                  placeholder="From Date"
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ minWidth: 140 }}
                />
                <Calendar
                  value={toDate}
                  onChange={(e) => handleFilterChange('toDate', e.value)}
                  placeholder="To Date"
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ minWidth: 140 }}
                />
                <Button
                  label="Clear"
                  icon="pi pi-times"
                  size="small"
                  outlined
                  severity="secondary"
                  onClick={onClear}
                />
                <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  loading={refreshingSla}
                  outlined
                  severity="secondary"
                  size="small"
                  onClick={onRefreshSla}
                />
                <Button
                  label="Export"
                  icon="pi pi-download"
                  size="small"
                  style={{
                    marginLeft: "auto",
                    background: "var(--blue)",
                    border: "none",
                  }}
                  onClick={onExport}
                />
              </div>
              <DataTable
                value={tickets}
                loading={loading}
                selection={selectedTickets}
                onSelectionChange={(e) => setSelectedTickets(e.value)}
                dataKey="id"
                rowClassName={(r) => (r.slaBreach ? "sla-breach" : "")}
                onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}?from=admin&tab=${activeTabIndex}`)}
                style={{ cursor: "pointer" }}
                lazy
                paginator
                rows={PAGE_SIZE}
                totalRecords={totalRecords}
                first={page * PAGE_SIZE}
                onPage={(e) => {
                  setPage(e.page);
                  loadTickets({}, e.page);
                }}
                paginatorTemplate="PrevPageLink PageLinks NextPageLink"
              >
                <Column selectionMode="multiple" style={{ width: 40 }} />
                <Column
                  field="ticketNo"
                  header="Ticket ID"
                  body={(r) => (
                    <strong
                      style={{
                        color: r.slaBreach ? "var(--red)" : "var(--blue)",
                      }}
                    >
                      {r.ticketNo}
                    </strong>
                  )}
                  style={{ width: 130 }}
                />
                <Column
                  field="subject"
                  header="Subject"
                  style={{ minWidth: 180 }}
                />
                <Column
                  field="raisedBy"
                  header="Raised By"
                  body={(r) =>
                    r.requester
                      ? `${r.requester.firstName} ${r.requester.lastName}`.trim()
                      : (r.raisedBy ?? "—")
                  }
                />
                <Column
                  field="assignedTo"
                  header="Agent"
                  body={(r) => formatTicketAgent(r)}
                />
                <Column
                  field="category"
                  header="Category"
                  body={(r) => r.category?.name ?? r.category ?? "—"}
                />
                <Column
                  field="priority"
                  header="Priority"
                  body={(r) => <PriorityTag value={r.priority} />}
                />
                <Column
                  field="status"
                  header="Status"
                  body={(r) => <StatusTag value={r.status} />}
                />
                <Column field="slaRemaining" header="SLA" body={(r) => {
                  const breached = r.slaBreach;
                  const pct = r.slaPct ?? 0;
                  const color = breached ? "var(--red)" : pct >= 80 ? "var(--orange)" : "var(--green)";
                  return (
                    <span style={{ color, fontWeight: 700, fontSize: 12 }}>
                      {breached ? "⚠ " : pct >= 80 ? "⏰ " : "✓ "}
                      {r.slaRemaining ?? "—"}
                    </span>
                  );
                }}
                />
                <Column header="CSAT" body={(r) => {
                  if (r.status !== "Resolved" || !r.csatRating)
                    return <span style={{ color: "var(--muted)" }}>—</span>;
                  return <Rating value={r.csatRating} readOnly cancel={false} style={{ fontSize: 11 }} />;
                }}
                />
                <Column field="createdAt" header="Raised On" body={(r) => fmtDate(r.createdAt)} />
                <Column field="updatedAt" header="Last Updated" body={(r) => fmtDate(r.updatedAt)} />
              </DataTable>
            </div>
          </TabPanel>
        )}

        {hasPermission(SUPPORT_ADMIN_ALLTICKETS_TAB_VIEW) && (
          <TabPanel header="All Tickets">
            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <strong style={{ fontSize: 13, color: "var(--navy)" }}>
                  All Tickets
                </strong>
                <div className="search-wrap">
                  <i className="pi pi-search" />
                  <InputText
                    placeholder="Search by Ticket ID or Subject…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onFilter()}
                    style={{ width: 220 }}
                  />
                </div>
                <Dropdown
                  value={statusFilter}
                  options={STATUS_OPTIONS}
                  onChange={(e) => handleFilterChange('status', e.value)}
                  placeholder="All Status"
                  style={{ minWidth: 130 }}
                />
                <Dropdown
                  value={agentFilter}
                  options={agents}
                  onChange={(e) => handleFilterChange('agent', e.value)}
                  placeholder="All Agents"
                  style={{ minWidth: 150 }}
                />
                <Dropdown
                  value={priorityFilter}
                  options={PRIORITY_OPTIONS}
                  onChange={(e) => handleFilterChange('priority', e.value)}
                  placeholder="All Priority"
                  style={{ minWidth: 130 }}
                />
                <Calendar
                  value={fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.value)}
                  placeholder="From Date"
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ minWidth: 140 }}
                />
                <Calendar
                  value={toDate}
                  onChange={(e) => handleFilterChange('toDate', e.value)}
                  placeholder="To Date"
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ minWidth: 140 }}
                />
                <Button
                  label="Clear"
                  icon="pi pi-times"
                  size="small"
                  outlined
                  severity="secondary"
                  onClick={onClear}
                />
                <Button
                  label="Export"
                  icon="pi pi-download"
                  size="small"
                  style={{
                    marginLeft: "auto",
                    background: "var(--blue)",
                    border: "none",
                  }}
                  onClick={onExport}
                />
              </div>
              <DataTable
                value={tickets}
                loading={loading}
                selection={selectedTickets}
                onSelectionChange={(e) => setSelectedTickets(e.value)}
                dataKey="id"
                rowClassName={(r) => (r.slaBreach ? "sla-breach" : "")}
                onRowClick={(e) => navigate(`/support-agent-work/${e.data.id}?from=admin&tab=${activeTabIndex}`)}
                style={{ cursor: "pointer" }}
                lazy
                paginator
                rows={PAGE_SIZE}
                totalRecords={totalRecords}
                first={page * PAGE_SIZE}
                onPage={(e) => {
                  setPage(e.page);
                  loadTickets({}, e.page);
                }}
                paginatorTemplate="PrevPageLink PageLinks NextPageLink"
              >
                <Column selectionMode="multiple" style={{ width: 40 }} />
                <Column
                  field="ticketNo"
                  header="Ticket ID"
                  body={(r) => (
                    <strong
                      style={{
                        color: r.slaBreach ? "var(--red)" : "var(--blue)",
                      }}
                    >
                      {r.ticketNo}
                    </strong>
                  )}
                  style={{ width: 130 }}
                />
                <Column
                  field="subject"
                  header="Subject"
                  style={{ minWidth: 180 }}
                />
                <Column
                  field="raisedBy"
                  header="Raised By"
                  body={(r) =>
                    r.requester
                      ? `${r.requester.firstName} ${r.requester.lastName}`.trim()
                      : (r.raisedBy ?? "—")
                  }
                />
                <Column
                  field="assignedTo"
                  header="Agent"
                  body={(r) => formatTicketAgent(r)}
                />
                <Column
                  field="category"
                  header="Category"
                  body={(r) => r.category?.name ?? r.category ?? "—"}
                />
                <Column
                  field="priority"
                  header="Priority"
                  body={(r) => <PriorityTag value={r.priority} />}
                />
                <Column
                  field="status"
                  header="Status"
                  body={(r) => <StatusTag value={r.status} />}
                />
                <Column field="slaRemaining" header="SLA" body={(r) => {
                  const breached = r.slaBreach;
                  const pct = r.slaPct ?? 0;
                  const color = breached ? "var(--red)" : pct >= 80 ? "var(--orange)" : "var(--green)";
                  return (
                    <span style={{ color, fontWeight: 700, fontSize: 12 }}>
                      {breached ? "⚠ " : pct >= 80 ? "⏰ " : "✓ "}
                      {r.slaRemaining ?? "—"}
                    </span>
                  );
                }}
                />
                <Column header="CSAT" body={(r) => {
                  if (r.status !== "Resolved" || !r.csatRating)
                    return <span style={{ color: "var(--muted)" }}>—</span>;
                  return <Rating value={r.csatRating} readOnly cancel={false} style={{ fontSize: 11 }} />;
                }}
                />
                <Column field="createdAt" header="Raised On" body={(r) => fmtDate(r.createdAt)} />
                <Column field="updatedAt" header="Last Updated" body={(r) => fmtDate(r.updatedAt)} />
              </DataTable>
            </div>
          </TabPanel>
        )}

        {hasPermission(SUPPORT_ADMIN_AGENT_TAB_VIEW) && (
          <TabPanel header="Agents">
            <AgentMgmt departmentId={deptFilterStr} />
          </TabPanel>
        )}

        {hasPermission(SUPPORT_ADMIN_CATEGORY_TAB_VIEW) && (
          <TabPanel header="Categories">
            <CategoryMaster departmentId={deptFilterStr} />
          </TabPanel>
        )}
        {hasPermission(SUPPORT_ADMIN_SLASETTING_TAB_VIEW) && (
          <TabPanel header="SLA Settings">
            <SLA departmentId={slaSelectedDept} />
          </TabPanel>
        )}
        {hasPermission(SUPPORT_ADMIN_REPORTS_TAB_VIEW) && (
          <TabPanel header="Reports">
            <Reports departmentId={deptFilterStr} />
          </TabPanel>
        )}
      </TabView>
    </div>
  );
};

export default AdminDashboard;